import { useEffect, useMemo, useState } from 'react';

import { useAppSelector, useAppDispatch } from 'redux/store';
import { getZonesPaginated } from '../../../../redux/slices/lists/zonesSlice';

import useAdvanceTable from 'hooks/useAdvanceTable';
import { useTableController } from 'hooks/useTableController';
import TableViewLayout from 'layouts/TableViewLayout';

import './dropdownStyle.css';
import { getActions, ActionButton, ActionDropdown, type ActionEntity } from 'actions';

const toActionEntity = (row: any): ActionEntity => ({ type: 'zone', id: row.zone_id, name: row.name });

const ListZones = () => {
  const dispatch = useAppDispatch();

  const { zones, config: headerConfig, filtersConfig, pagination, status, error, loading } = useAppSelector((state) => state.listOfZones);

  const userData = useAppSelector((state) => state.auth.userData);

  const pageName = 'zones';
  const [showSidebar, setShowSidebar] = useState(false);

  const isLoading = loading.getZonesPaginated;

  useEffect(() => {
    if (status === null) {
      dispatch(getZonesPaginated({}) as any);
    }
  }, [dispatch, status]);

  const {
    filters,
    setFilterAndSync,
    resetFilters,
    updateSort,
    updatePage,
    updateLimit,
    limit,
    updateUserSettings,
    getFormattedData,
    generateColumns,
    refresh,
    isSilentRefresh,
  } = useTableController({
    userData,
    pageName,
    filtersConfig,
    headerConfig,
    fetchData: getZonesPaginated,
    getFormattedRow: (row: any) => ({ ...row }),
  });

  const actionsColumn = {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }: any) => {
      const actions = getActions({ entities: [toActionEntity(row.original)], refresh });
      return <ActionDropdown actions={actions} items={['edit', 'delete', 'addToTransportZone']} />;
    },
  };

  const baseColumns = generateColumns((key: string, order: 'asc' | 'desc') => {
    updateSort(key, order);
    updateUserSettings(key, order);
  });
  const columns = [...baseColumns, actionsColumn];

  const formattedData = useMemo(() => getFormattedData(zones || []), [zones]);

  const table = useAdvanceTable({
    data: formattedData,
    columns,
    pageSize: limit,
    pagination: true,
    sortable: true,
    selection: true,
    getRowId: (row) => String(row.zone_id),
  });

  const { currentPage, totalPages, totalItems } = pagination;

  const selectionEntities = table.getSelectedRowModel().rows.map((r: any) => toActionEntity(r.original));
  const toolbarActions = getActions({ entities: selectionEntities, refresh });

  return (
    <TableViewLayout
      filtersConfig={filtersConfig}
      filters={filters}
      pageName={pageName}
      columns={(headerConfig ?? []).map((c: any) => ({
        key: c.header_key,
        label: c.label,
      }))}
      pageSize={limit}
      error={error}
      onFilterChange={setFilterAndSync}
      onResetFilters={resetFilters}
      onPageSizeChange={updateLimit}
      currentPage={currentPage}
      totalPages={totalPages}
      totalItems={totalItems}
      onPageChange={updatePage}
      table={table}
      showSidebar={showSidebar}
      setShowSidebar={setShowSidebar}
      onRefresh={refresh}
      isLoading={isLoading}
      isSilentRefresh={isSilentRefresh}
      toolbarButtons={
        <>
          <ActionButton actions={toolbarActions} action="createZone" variant="primary" />
          <ActionButton actions={toolbarActions} action="addToTransportZone" className="ms-2" variant="secondary" label="Add selected to transport" />
        </>
      }
    />
  );
};

export default ListZones;
